import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/app/AuthContext";
import { Loader2 } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { Seo } from "@/components/site/Seo";

const DEMOS = [
  { label: "Learner", email: "learner@demo.opendegree.co.uk" },
  { label: "Provider", email: "provider@demo.opendegree.co.uk" },
  { label: "Partner", email: "partner@demo.opendegree.co.uk" },
  { label: "Admin", email: "admin@demo.opendegree.co.uk" },
];
const DEMO_PW = "OD-Demo-2026!x7Kp";
const DEMO_ENABLED = process.env.REACT_APP_ENABLE_DEMO_LOGINS !== "false";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) navigate("/app");
    else setError(res.error);
  };

  const quick = (dEmail) => { setEmail(dEmail); setPassword(DEMO_PW); };

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center px-5 py-12">
      <Seo noindex title="Sign in — OpenDegree" description="Sign in to the OpenDegree platform." path="/app/login" />
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2.5 mb-8">
          <Logo markClass="h-11 w-11" textClass="text-2xl" />
        </Link>
        <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_8px_30px_rgba(10,37,64,0.06)]">
          <h1 className="font-display text-2xl font-bold tracking-tight text-ink">Sign in</h1>
          <p className="mt-1 text-sm text-slatey">Access your OpenDegree workspace.</p>
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
            <div>
              <label className="text-sm font-medium text-ink">Email</label>
              <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-[15px] outline-none focus:border-cobalt focus:ring-2 focus:ring-cobalt/20" placeholder="you@example.com" />
            </div>
            <div>
              <label className="text-sm font-medium text-ink">Password</label>
              <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-[15px] outline-none focus:border-cobalt focus:ring-2 focus:ring-cobalt/20" placeholder="••••••••" />
            </div>
            {error && <p data-testid="login-error" className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-600">{error}</p>}
            <button type="submit" disabled={loading} data-testid="login-submit"
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-cobalt px-6 py-3.5 text-[15px] font-semibold text-white transition-transform duration-200 hover:scale-[1.01] active:scale-[0.99] disabled:opacity-60">
              {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Signing in…</> : "Sign in"}
            </button>
          </form>
          {DEMO_ENABLED && (
          <div className="mt-6 border-t border-slate-100 pt-4">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slatey">Demo accounts (test only)</p>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {DEMOS.map((d) => (
                <button key={d.email} type="button" onClick={() => quick(d.email)} data-testid={`demo-${d.label.toLowerCase()}`}
                  className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-ink hover:bg-surface">{d.label}</button>
              ))}
            </div>
            <p className="mt-2 text-[11px] text-slatey">Tap a role, then Sign in. Shared demo password is pre-filled. Preview/testing only.</p>
          </div>
          )}
          <p className="mt-5 text-center text-sm text-slatey">
            New to OpenDegree? <Link to="/app/register" data-testid="login-register-link" className="font-bold text-cobalt">Start your journey</Link>
          </p>
        </div>

      </div>
    </div>
  );
}
